import { useState } from 'react';
import { useCart } from '@/context/CartContext';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Truck } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

export default function CheckoutPage() {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', state: '', pincode: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const shipping = totalPrice >= 499 ? 0 : 49;
  const grandTotal = totalPrice + shipping;

  if (items.length === 0) {
    return (
      <div className="bg-[#0C4A8C] min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/40 font-sans text-sm mb-4">Your cart is empty</p>
          <button onClick={() => navigate('/shop')} data-testid="checkout-empty-shop-btn"
            className="bg-[#F0F9FF] text-[#0C4A8C] px-6 py-2.5 rounded-[2px] text-[11px] font-sans uppercase tracking-[3px] hover:bg-white transition-colors">
            Shop Now
          </button>
        </div>
      </div>
    );
  }

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const orderItems = items.map(i => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity, size: i.size || '' }));
      const { data } = await axios.post(`${API}/api/orders/create`, {
        items: orderItems,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        shipping_address: form.address,
        shipping_city: form.city,
        shipping_state: form.state,
        shipping_pincode: form.pincode
      });

      if (data.razorpay_configured && data.razorpay_order_id) {
        const options = {
          key: data.razorpay_key_id,
          amount: data.amount,
          currency: data.currency,
          name: 'TRUFORM MEN',
          description: `Order ${data.order_id}`,
          order_id: data.razorpay_order_id,
          handler: async (response) => {
            try {
              await axios.post(`${API}/api/orders/verify`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_id: data.order_id
              });
              clearCart();
              navigate(`/order-confirmation/${data.order_id}`);
            } catch {
              setError('Payment verification failed. Please contact support.');
            }
          },
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: '#0C4A8C' },
          modal: { ondismiss: () => setSubmitting(false) }
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', () => { setError('Payment failed. Please try again.'); setSubmitting(false); });
        rzp.open();
      } else {
        await axios.post(`${API}/api/orders/confirm-demo`, { order_id: data.order_id });
        clearCart();
        navigate(`/order-confirmation/${data.order_id}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Checkout failed. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F0F9FF]">
      <section className="bg-[#0C4A8C] py-10 md:py-14">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="eyebrow mb-3 text-[#BAE6FD]">Checkout</p>
          <h1 data-testid="checkout-heading" className="text-3xl font-serif font-medium text-white tracking-tight mb-8">Complete your order.</h1>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Form */}
            <form onSubmit={handleCheckout} data-testid="checkout-form" className="lg:col-span-3 bg-white rounded-[2px] p-6">
              {error && <div data-testid="checkout-error" className="bg-red-50 border border-red-200 text-red-700 text-[12px] font-sans px-3 py-2 rounded-[2px] mb-4">{error}</div>}

              <h2 className="font-serif text-lg text-[#0C4A8C] font-medium mb-5">Shipping Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <CField label="Full Name" testid="checkout-name" value={form.name} onChange={v => set('name', v)} required />
                <CField label="Email" testid="checkout-email" type="email" value={form.email} onChange={v => set('email', v)} required />
                <CField label="Phone" testid="checkout-phone" type="tel" value={form.phone} onChange={v => set('phone', v)} required placeholder="10-digit mobile" />
                <CField label="Pincode" testid="checkout-pincode" value={form.pincode} onChange={v => set('pincode', v)} required placeholder="6-digit" />
              </div>
              <CField label="Address" testid="checkout-address" value={form.address} onChange={v => set('address', v)} required className="mb-4" placeholder="House no., street, area" />
              <div className="grid grid-cols-2 gap-4 mb-6">
                <CField label="City" testid="checkout-city" value={form.city} onChange={v => set('city', v)} required />
                <CField label="State" testid="checkout-state" value={form.state} onChange={v => set('state', v)} required />
              </div>

              <div className="flex items-center gap-4 mb-5 text-[11px] font-sans text-[#6B7D8A]">
                <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#0C7BC0]" /> Secure checkout</span>
                <span className="flex items-center gap-1.5"><Truck size={14} className="text-[#0C7BC0]" /> {shipping === 0 ? 'Free shipping' : 'Delivery in 3-5 days'}</span>
              </div>

              <button data-testid="place-order-btn" type="submit" disabled={submitting}
                className="w-full bg-[#0C4A8C] text-[#F0F9FF] py-3.5 rounded-[2px] text-[11px] font-sans font-medium uppercase tracking-[3px] hover:bg-[#093B70] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {submitting ? 'Processing...' : <>{RAZORPAY_KEY && !RAZORPAY_KEY.includes('PLACEHOLDER') ? 'Pay' : 'Place Order'} &#8377;{grandTotal} <ArrowRight size={14} /></>}
              </button>

              {(!RAZORPAY_KEY || RAZORPAY_KEY.includes('PLACEHOLDER')) && (
                <p className="text-center text-[10px] text-[#6B7D8A] mt-2 font-sans">Demo mode — no payment will be charged</p>
              )}
            </form>

            {/* Order Summary */}
            <div className="lg:col-span-2">
              <div className="bg-[#F0F9FF] rounded-[2px] p-6 border" style={{ borderColor: 'rgba(12,74,140,0.08)' }} data-testid="checkout-summary">
                <h3 className="font-serif text-base text-[#0C4A8C] font-medium mb-4">Order Summary</h3>
                {items.map(item => (
                  <div key={item.id} data-testid={`checkout-item-${item.id}`} className="flex justify-between py-2.5" style={{ borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
                    <div>
                      <p className="text-sm text-[#0C4A8C] font-sans">{item.name}</p>
                      <p className="text-[11px] text-[#6B7D8A] font-sans">Qty: {item.quantity} {item.size && `· ${item.size}`}</p>
                    </div>
                    <span className="text-sm text-[#0C4A8C] font-sans">&#8377;{item.price * item.quantity}</span>
                  </div>
                ))}
                <div className="flex justify-between py-2.5" style={{ borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
                  <span className="text-[12px] text-[#6B7D8A] font-sans">Subtotal</span>
                  <span className="text-sm text-[#0C4A8C] font-sans">&#8377;{totalPrice}</span>
                </div>
                <div className="flex justify-between py-2.5" style={{ borderBottom: '1px solid rgba(12,74,140,0.06)' }}>
                  <span className="text-[12px] text-[#6B7D8A] font-sans">Shipping</span>
                  <span className="text-sm text-[#0C4A8C] font-sans">{shipping === 0 ? 'FREE' : `₹${shipping}`}</span>
                </div>
                <div className="flex justify-between py-3">
                  <span className="text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C7BC0]">Total</span>
                  <span data-testid="checkout-total" className="text-lg font-serif font-medium text-[#0C4A8C]">&#8377;{grandTotal}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function CField({ label, testid, value, onChange, type = 'text', required = false, className = '', placeholder = '' }) {
  return (
    <div className={className}>
      <label className="block text-[11px] font-sans font-medium uppercase tracking-[2px] text-[#0C4A8C]/60 mb-1.5">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full px-3 py-2.5 border rounded-[2px] text-sm font-sans text-[#0C4A8C] focus:outline-none focus:border-[#0C4A8C] transition-colors" style={{ borderColor: 'rgba(12,74,140,0.12)' }} />
    </div>
  );
}
